export interface WhyUsItem {
  icon: string;
  title: string;
  description: string;
}

export interface PageWhyUsProps {
  heading: string;
  subheading?: string;
  intro?: string;
  items: WhyUsItem[];
  imageSrc: string;
  imageCaption?: string;
}

export default function PageWhyUs({
  heading,
  subheading,
  intro,
  items,
  imageSrc,
  imageCaption,
}: PageWhyUsProps) {
  return (
    <section className="bg-parchment py-24 lg:py-32 overflow-hidden">
      <div className="max-w-screen-xl mx-auto px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-16 lg:gap-20 items-stretch">
          {/* Image column */}
          <div className="lg:col-span-2 relative order-2 lg:order-1">
            <div className="sticky top-28">
              <div className="relative">
                {/* Offset decorative frame */}
                <div className="absolute -top-4 -left-4 w-full h-full border border-antique/20 pointer-events-none" />
                <div className="aspect-[3/4] overflow-hidden">
                  <img
                    src={imageSrc}
                    alt="Yarnell Emporium interior"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                {/* Caption */}
                {imageCaption && (
                  <div className="mt-5 flex items-center gap-3">
                    <span className="w-6 h-px bg-antique" />
                    <p className="text-xs text-pewter font-sans italic tracking-wide">{imageCaption}</p>
                  </div>
                )}
              </div>

              {/* Info card below image */}
              <div className="mt-6 p-6 bg-inkblack">
                <p className="text-antique text-xs font-sans font-600 tracking-[0.2em] uppercase mb-3">Visit Us</p>
                <p className="text-ivory/60 text-sm font-sans font-300 leading-relaxed mb-1">22899 S Hwy 89, Yarnell, AZ</p>
                <p className="text-ivory/60 text-sm font-sans font-300">Open Daily: 9am – 5pm</p>
                <a
                  href="tel:9284279593"
                  className="inline-flex items-center gap-2 mt-4 text-antique text-xs font-sans font-600 tracking-[0.14em] uppercase hover:text-white transition-colors cursor-pointer"
                >
                  <i className="ri-phone-line" /> 928.427-9593
                </a>
              </div>
            </div>
          </div>

          {/* Content column */}
          <div className="lg:col-span-3 order-1 lg:order-2">
            {subheading && (
              <div className="flex items-center gap-3 mb-4">
                <span className="w-10 h-px bg-antique" />
                <span className="text-antique text-xs font-sans font-500 tracking-[0.24em] uppercase">{subheading}</span>
              </div>
            )}
            <h2
              className="font-serif text-inkblack mb-5"
              style={{ fontSize: 'clamp(1.8rem, 3.5vw, 3rem)', fontWeight: 700, lineHeight: 1.08, letterSpacing: '-0.015em' }}
            >
              {heading}
            </h2>
            {intro && (
              <p className="text-pewter text-sm font-sans font-300 leading-relaxed mb-12 max-w-xl">{intro}</p>
            )}

            {/* Items in 2-column grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-0">
              {items.map((item, i) => (
                <div
                  key={i}
                  className={`py-7 pr-8 ${
                    i % 2 === 0 ? 'sm:border-r border-mist' : ''
                  } ${
                    i < items.length - 2 ? 'border-b border-mist' : ''
                  } ${
                    i >= 2 ? 'pt-7' : ''
                  }`}
                >
                  <div className="w-9 h-9 flex items-center justify-center border border-antique/30 mb-4 bg-parchment">
                    <i className={`${item.icon} text-antique text-sm`} />
                  </div>
                  <h3 className="font-sans font-600 text-inkblack text-sm mb-2 leading-snug tracking-wide">{item.title}</h3>
                  <p className="text-pewter text-xs font-sans font-300 leading-relaxed">{item.description}</p>
                </div>
              ))}
            </div>

            {/* CTA below items */}
            <div className="mt-12 pt-8 border-t border-mist flex flex-wrap items-center gap-5">
              <a
                href="https://maps.google.com/?q=22899+S+Hwy+89+Yarnell+AZ+85362"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-inkblack text-ivory text-xs font-sans font-600 tracking-[0.16em] uppercase px-8 py-4 whitespace-nowrap hover:bg-antique hover:text-inkblack transition-colors cursor-pointer"
              >
                Get Directions
              </a>
              <a
                href="tel:9284279593"
                className="inline-flex items-center gap-2 text-inkblack text-xs font-sans font-600 tracking-[0.14em] uppercase border-b border-inkblack pb-0.5 hover:text-antique hover:border-antique transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-phone-line" /> Call Now
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
